import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { setLoading } from '../redux/loaderAction';
import Loader from '../components/loader/Loader';
import SaveButton from '../components/buttons/SaveButton';
import CancelButton from '../components/buttons/CancelButton';
import Swal from '../messageBox/customSweetAlert';
import { currentDateFormat } from '../utils';
import {
  CREATE_BILL_RATE_CONFIGURATIONV1,
  GET_SERVICE_NAMES,
  GET_ALL_PAYER,
  GET_LOCATION_TYPE,
  GET_STATE_URI,
} from '../constant';
import { billRateValidation } from "../validation/Validation";

/** Format Date as MM/DD/YYYY (zero-padded) for v1 API */
function formatDateMMDDYYYY(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

function formatCodeWithModifier(code, modifier) {
  const codeStr = code ?? '';
  const mod = modifier ?? '';
  if (mod !== '' && String(mod).trim() !== '') {
    return `${codeStr} - ${String(mod).trim()}`;
  }
  return codeStr;
}

/** Map API field names to form field keys used in the UI */
const API_FIELD_TO_FORM_FIELD = {
  start_date: 'effective_date',
  end_date: 'end_date',
};

function mapApiDetailsToErrors(details) {
  if (!Array.isArray(details)) return {};
  return details.reduce((acc, item) => {
    const apiField = item?.field;
    const formField = API_FIELD_TO_FORM_FIELD[apiField] || apiField;
    if (formField && item?.message) {
      acc[formField] = item.message;
    }
    return acc;
  }, {});
}

/**
 * Classify v1 bill-rate API result by HTTP status + response body.
 * 201 Created | 409 Conflict | 400 Bad Request | 500 Internal Server Error
 */
function parseBillRateV1ApiResult(httpStatus, body) {
  const status = httpStatus != null ? Number(httpStatus) : null;
  const message =
    body?.message ||
    'Failed to save bill rate configuration. Please try again.';

  // HTTP 201 — { status: 201, success: true, message: "Created", data: {...} }
  if (
    status === 201 ||
    body?.status === 201 ||
    body?.status === '201'
  ) {
    if (body?.success === true || body?.data != null) {
      return {
        type: 'success',
        message: "Bill Rate Configuration has been saved successfully.",
      };
    }
  }

  // HTTP 409 — { message, success: false, code: 409 }
  if (status === 409 || body?.code === 409) {
    return {
      type: 'duplicate',
      message: message || 'Bill rate configuration already exists',
    };
  }

  // HTTP 400 — { status: 400, error, message, details: [...] }
  if (status === 400 || body?.status === 400 || body?.status === '400') {
    const validationMessage = body?.error
      ? `${body.error}: ${message}`
      : message;
    return {
      type: 'validation',
      message: validationMessage || 'Invalid data',
      details: body?.details,
    };
  }

  // HTTP 500 — internal server error
  if (status === 500) {
    const serverMessage = body?.error
      ? `${body.error}: ${message}`
      : message;
    return {
      type: 'server',
      message: serverMessage || 'Internal server error. Please try again later.',
    };
  }

  // Fallback when HTTP status missing but body indicates conflict
  if (body?.success === false && body?.code === 409) {
    return {
      type: 'duplicate',
      message: message || 'Bill rate configuration already exists',
    };
  }

  return { type: 'error', message };
}

function handleBillRateV1ApiResult(httpStatus, body, handlers) {
  const result = parseBillRateV1ApiResult(httpStatus, body);
  dispatchBillRateV1Result(result, handlers);
}

function dispatchBillRateV1Result(result, handlers) {
  switch (result.type) {
    case 'success':
      handlers.onSuccess(result.message);
      break;
    case 'duplicate':
      handlers.onDuplicate(result.message);
      break;
    case 'validation':
      handlers.onValidation(result.message, result.details);
      break;
    case 'server':
      handlers.onServerError(result.message);
      break;
    default:
      handlers.onGenericError(result.message);
      break;
  }
}

function BillRateConfigurationV1() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((state) => state.loader.loading);
  const [currentDate, setCurrentDate] = useState(new Date());

  const [formData, setFormData] = useState({
    service_id: '',
    procedure_code: '',
    base_code: '',
    milage_code: '',
    state: '',
    payer_id: '',
    status: '1',
    base_bill_rate: '',
    mileage_bill_rate: '0.00',
    effective_date: '',
    end_date: '',
    notes: '',
  });

  const [effectiveDate, setEffectiveDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');

  const [states, setStateData] = useState([]);
  const [serviceNames, setServiceNames] = useState([]);
  const [loadingServiceDetails, setLoadingServiceDetails] = useState(false);
  const [payerList, setPayerList] = useState([]);

  const loadUser = async () => {
    const response1 = await axios.get(GET_STATE_URI);
    const response2 = await axios.get(GET_LOCATION_TYPE);
    const response3 = await axios.get(GET_SERVICE_NAMES, {
      params: { id: 2 },
    });
    const response4 = await axios.get(GET_ALL_PAYER);
    setStateData(response1.data);
    setServiceNames(response3.data.data || []);
    setPayerList(response4.data || []);
  };

  const fetchServiceDetails = async (serviceId) => {
    try {
      if (!serviceId) {
        setLoadingServiceDetails(false);
        return;
      }

      setLoadingServiceDetails(true);
      const selectedService = serviceNames.find((service) => service.id == serviceId);

      if (selectedService) {
        setFormData((prev) => ({
          ...prev,
          procedure_code: selectedService.procedure_code || '',
          base_code: formatCodeWithModifier(
            selectedService.base_code,
            selectedService.modifier,
          ),
          milage_code: formatCodeWithModifier(
            selectedService.milage_code,
            selectedService.milage_modifier,
          ),
        }));
      }
    } catch (error) {
      console.error('Error fetching service details:', error);
    } finally {
      setLoadingServiceDetails(false);
    }
  };

  useEffect(() => {
    loadUser();
    setCurrentDate(new Date());
  }, []);

  useEffect(() => {
    if (formData.service_id) {
      fetchServiceDetails(formData.service_id);
    } else {
      setFormData((prev) => ({
        ...prev,
        procedure_code: '',
        base_code: '',
        milage_code: '',
      }));
    }
  }, [formData.service_id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setApiError('');
    setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const buildV1Payload = () => {
    const startDate = formatDateMMDDYYYY(effectiveDate);
    const endDateFormatted = formatDateMMDDYYYY(endDate);

    return {
      service_id: parseInt(formData.service_id),
      state_id: parseInt(formData.state),
      payer_id: parseInt(formData.payer_id),
      bill_status: formData.status,
      base_bill_rate: parseFloat(formData.base_bill_rate),
      mileage_bill_rate: parseFloat(formData.mileage_bill_rate),
      start_date: startDate,
      end_date: endDateFormatted,
      notes: formData.notes,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');

    const validationData = {
      ...formData,
      effective_date: effectiveDate ? formatDateMMDDYYYY(effectiveDate) : '',
      end_date: endDate ? formatDateMMDDYYYY(endDate) : '',
    };

    const isValid = billRateValidation(validationData);
    setErrors(isValid);

    if (Object.keys(isValid).length > 0) {
      return;
    }

    const responseHandlers = {
      onSuccess: (message) => {
        Swal.fire({
          title: 'Success!',
          text: message || 'Bill Rate Configuration has been saved successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
          allowOutsideClick: false,
        }).then(() => {
          navigate('/home/billing/bill-rate-list', { replace: true });
        });
      },
      onDuplicate: (message) => {
        Swal.fire({
          title: 'Duplicate Configuration',
          text: message || 'Bill rate configuration already exists',
          icon: 'warning',
          confirmButtonText: 'OK',
          allowOutsideClick: false,
        });
      },
      onValidation: (message, details) => {
        const fieldErrors = mapApiDetailsToErrors(details);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldErrors }));
        }
        setApiError(message);
      },
      onServerError: (message) => {
        Swal.fire({
          title: 'Server Error',
          text: message || 'Internal server error. Please try again later.',
          icon: 'error',
          confirmButtonText: 'OK',
          allowOutsideClick: false,
        });
      },
      onGenericError: (message) => {
        Swal.fire({
          title: 'Error!',
          text: message,
          icon: 'error',
          confirmButtonText: 'OK',
          allowOutsideClick: false,
        });
      },
    };

    try {
      dispatch(setLoading(true));
      const payload = buildV1Payload();
      const response = await axios.post(
        CREATE_BILL_RATE_CONFIGURATIONV1,
        payload,
      );
      dispatch(setLoading(false));
      handleBillRateV1ApiResult(response.status, response.data, responseHandlers);
    } catch (error) {
      dispatch(setLoading(false));

      const httpStatus = error?.response?.status;
      const body = error?.response?.data;

      if (httpStatus && body) {
        handleBillRateV1ApiResult(httpStatus, body, responseHandlers);
      } else {
        responseHandlers.onGenericError(
          error?.message ||
            'Failed to save bill rate configuration. Please try again.',
        );
      }
    }
  };

  const handleClearForm = () => {
    setFormData({
      service_id: '',
      procedure_code: '',
      base_code: '',
      milage_code: '',
      state: '',
      payer_id: '',
      status: '1',
      base_bill_rate: '',
      mileage_bill_rate: '0.00',
      effective_date: '',
      end_date: '',
      notes: '',
    });
    setEffectiveDate(null);
    setEndDate(null);
    setErrors({});
    setApiError('');
  };

  const handleEffectiveDateChange = (date) => {
    setApiError('');
    setErrors((prev) => ({ ...prev, effective_date: null }));
    setEffectiveDate(date);
    setFormData((prev) => ({
      ...prev,
      effective_date: formatDateMMDDYYYY(date),
    }));
  };

  const handleEndDateChange = (date) => {
    setApiError('');
    setErrors((prev) => ({ ...prev, end_date: null }));
    setEndDate(date);
    setFormData((prev) => ({
      ...prev,
      end_date: formatDateMMDDYYYY(date),
    }));
  };

  return (
    <>
      <div className="tab-pane fade active show" id="add-bill-rate-v1" role="tabpanel" aria-labelledby="home-tab">
        <section>
          <div className='d-flex justify-content-between form-container'>
            <div>
              <h2 className="fs-5 font-weight-600">Add New Bill Rate</h2>
            </div>
            <div>
              <span>{currentDateFormat(currentDate)}</span>
            </div>
          </div>
        </section>

        <section>
          <div className='bg-white px-5 py-4'>
            {apiError && (
              <div className="alert alert-danger mb-3" role="alert">
                {apiError}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="row mb-4">
                <div className="col-lg-4 col-md-6 mb-3">
                  <label className='require-field'>Service Name</label>
                  <select
                    className={`form-select ${errors.service_id ? 'is-invalid' : ''}`}
                    name="service_id"
                    value={formData.service_id}
                    onChange={handleInputChange}
                    disabled={loadingServiceDetails}
                  >
                    <option value="">
                      {loadingServiceDetails ? 'Loading services...' : 'Select Service Name'}
                    </option>
                    {serviceNames.map((service, index) => (
                      <option key={index} value={service.id}>
                        {service.service_name}
                      </option>
                    ))}
                  </select>
                  {errors.service_id && <div className="errors">{errors.service_id}</div>}
                </div>

                <div className="col-lg-2 col-md-4 mb-3">
                  <label className='require-field'>Base Code</label>
                  <input
                    type="text"
                    className={`form-control ${errors.base_code ? 'is-invalid' : ''}`}
                    name="base_code"
                    value={formData.base_code}
                    onChange={handleInputChange}
                    placeholder={loadingServiceDetails ? 'Loading...' : 'Enter base code'}
                    autoComplete='off'
                    disabled
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                  {errors.base_code && <div className="invalid-feedback">{errors.base_code}</div>}
                </div>

                <div className="col-lg-2 col-md-4 mb-3">
                  <label className='require-field'>Mileage Code</label>
                  <input
                    type="text"
                    className={`form-control ${errors.milage_code ? 'is-invalid' : ''}`}
                    name="milage_code"
                    value={formData.milage_code}
                    onChange={handleInputChange}
                    placeholder={loadingServiceDetails ? 'Loading...' : 'Enter mileage code'}
                    autoComplete='off'
                    disabled
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                  {errors.milage_code && <div className="invalid-feedback">{errors.milage_code}</div>}
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-lg-4 col-md-6 mb-3">
                  <label className='require-field'>State</label>
                  <select
                    className={`form-select ${errors.state ? 'is-invalid' : ''}`}
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                  >
                    <option value="">Select State</option>
                    {states.map((state, index) => (
                      <option key={index} value={state.state_id || state.id}>
                        {state.state_name || state.name}
                      </option>
                    ))}
                  </select>
                  {errors.state && <div className="invalid-feedback">{errors.state}</div>}
                </div>

                <div className="col-lg-4 col-md-6 mb-3">
                  <label className='require-field'>Payer Name</label>
                  <select
                    className={`form-select ${errors.payer_id ? 'is-invalid' : ''}`}
                    name="payer_id"
                    value={formData.payer_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Payer</option>
                    {payerList.map((payer, index) => (
                      <option key={index} value={payer.id}>
                        {payer.payer_name}
                      </option>
                    ))}
                  </select>
                  {errors.payer_id && <div className="invalid-feedback">{errors.payer_id}</div>}
                </div>

                <div className="col-lg-4 col-md-6 mb-3">
                  <label>Status</label>
                  <select
                    className="form-select"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-lg-4 col-md-6 mb-3">
                  <label className='require-field'>Base Bill Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    className={`form-control ${errors.base_bill_rate ? 'is-invalid' : ''}`}
                    name="base_bill_rate"
                    value={formData.base_bill_rate}
                    onChange={handleInputChange}
                    placeholder="Enter base bill rate"
                    autoComplete='off'
                  />
                  {errors.base_bill_rate && <div className="invalid-feedback">{errors.base_bill_rate}</div>}
                </div>

                <div className="col-lg-4 col-md-6 mb-3">
                  <label className='require-field'>Mileage Bill Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    className={`form-control ${errors.mileage_bill_rate ? 'is-invalid' : ''}`}
                    name="mileage_bill_rate"
                    value={formData.mileage_bill_rate}
                    onChange={handleInputChange}
                    placeholder="Enter mileage bill rate"
                    autoComplete='off'
                  />
                  {errors.mileage_bill_rate && <div className="invalid-feedback">{errors.mileage_bill_rate}</div>}
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-lg-4 col-md-6 mb-3">
                  <div>
                    <label className='require-field d-block'>Effective Date</label>
                    <DatePicker
                      selected={effectiveDate}
                      onChange={handleEffectiveDateChange}
                      dateFormat="MM/dd/yyyy"
                      className={`form-control ${errors.effective_date ? 'is-invalid' : ''}`}
                      placeholderText="Select effective date"
                      isClearable
                      minDate={new Date()}
                    />
                    {errors.effective_date && (
                      <div className="errors">{errors.effective_date}</div>
                    )}
                  </div>
                </div>

                <div className="col-lg-4 col-md-6 mb-3">
                  <div>
                    <label className='require-field d-block'>End Date</label>
                    <DatePicker
                      selected={endDate}
                      onChange={handleEndDateChange}
                      dateFormat="MM/dd/yyyy"
                      className={`form-control ${errors.end_date ? 'is-invalid' : ''}`}
                      placeholderText="Select end date"
                      isClearable
                      minDate={effectiveDate || new Date()}
                    />
                    {errors.end_date && (
                      <div className="errors">{errors.end_date}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-12">
                  <label className='require-field'>Notes</label>
                  <textarea
                    className="form-control"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Additional notes or comments"
                    rows="4"
                    style={{ resize: 'both' }}
                  />
                </div>
              </div>

              <div className='footer-content'>
                <SaveButton type='submit' buttonTitle={'Add Rate'} />
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={handleClearForm}
                >
                  Clear Form
                </button>
                <CancelButton
                  cancelHandler={() => navigate('/home/billing/list', { replace: true })}
                />
              </div>
            </form>
          </div>
        </section>
      </div>

      <div>
        {loading && <Loader />}
      </div>
    </>
  );
}

export default BillRateConfigurationV1;
